import { MapProvider, useMap } from "@vis.gl/react-maplibre";
import { useEffect, useMemo, useRef, useState } from "react";
import { getMapFitPadding, LIST_MAP_STYLE } from "../../lib/mapConfig";
import { regionById } from "../../lib/regions";
import type {
  Coords,
  EnrichedSpot,
  Level,
  Region,
  ScoredDay,
} from "../../lib/types";
import { BaseMap } from "./BaseMap";
import { CustomLocationMarker, RegionMarker, SpotMarker } from "./MapMarkers";

interface SpotsMapProps {
  /** Spots already filtered to the active region. */
  spots: EnrichedSpot[];
  /** The region these spots belong to — used for the fallback region marker. */
  regionId: Region;
  /** Day driving the marker colour and arrow direction (dayOffset). */
  selectedDay: number;
  /** Currently selected spot id (controlled). The parent OverviewPage owns
   *  this state so the side list can react to map marker clicks (scroll +
   *  outline the matching card). Null = no selection. */
  selectedSpotId: string | null;
  /** Currently hovered spot in the side list (desktop affordance). Scales
   *  the matching marker so the user sees where on the map a card sits.
   *  Null = no list-hover. */
  hoveredSpotId?: string | null;
  /** Called when the user clicks a marker. Receives the next selection
   *  (toggles off if the same marker is clicked twice). */
  onSelectSpot: (spotId: string | null) => void;
  /** Optional dropped-pin coordinates — when set, renders a pin at this
   *  location (long-press by the user on the map). */
  customLocation: Coords | null;
  /** Fires when the user long-presses (or right-clicks on desktop) the
   *  map. Receives the lat/lon of the press. */
  onLongPressMap: (coords: Coords) => void;
}

/** Per-spot snapshot for the currently selected day. Skip days (no
 *  daytime wind worth showing) collapse to `dirDeg=null` and the
 *  `unlikely` colour so the marker still reads as "poor conditions"
 *  instead of disappearing or looking like a missing data point.
 *
 *  For today specifically (whichever day contains the `isNow` hour),
 *  the marker arrow tracks the live current hour rather than the
 *  day's representative direction — once the morning has passed,
 *  the day-average tells the user what blew earlier, not what's
 *  blowing now. */
function dayMarkerData(spot: EnrichedSpot, dayOffset: number): {
  dirDeg: number | null;
  speedMs: number | null;
  level: Exclude<Level, "skip">;
} {
  const day: ScoredDay | undefined =
    spot.days.find((d) => d.dayOffset === dayOffset) ?? spot.days[0];
  if (!day || day.level === "skip") {
    return { dirDeg: null, speedMs: null, level: "unlikely" };
  }
  const nowHour = day.hours.find((h) => h.isNow);
  // Same pattern as direction: for today (whichever day contains the
  // isNow hour) track the live current hour so the marker reflects
  // what's blowing right now. For other days fall back to the day's
  // midpoint speed — a single number the arc can encode without
  // implying a particular hour. (Mean of hours would be smoother but
  // requires iterating; midpoint matches what users already see in the
  // forecast card's day range.)
  const speedMs =
    nowHour?.speedMs ?? (day.speedMinMs + day.speedMaxMs) / 2;
  return {
    dirDeg: nowHour?.dirDeg ?? day.dirDeg,
    speedMs,
    level: day.level,
  };
}

/**
 * Spots map for one region. Renders individual `SpotMarker`s when the map
 * is zoomed in enough to keep markers from overlapping; otherwise drops
 * back to a single `RegionMarker` with the spot count. Tapping the region
 * marker zooms in to the spot bounds.
 *
 * The component restores the last-known map viewport from session storage
 * so navigating from the regions map → spots map feels continuous.
 */
export function SpotsMap({
  spots,
  regionId,
  selectedDay,
  selectedSpotId,
  hoveredSpotId,
  onSelectSpot,
  customLocation,
  onLongPressMap,
}: SpotsMapProps) {
  const region = regionById(regionId);
  const initial = region?.coords ?? { lat: 62.5, lon: 16.5 };

  return (
    <MapProvider>
      <BaseMap
        mapStyle={LIST_MAP_STYLE}
        initialLongitude={initial.lon}
        initialLatitude={initial.lat}
        initialZoom={6}
        showGeolocate
      >
        <SpotsLayer
          spots={spots}
          regionId={regionId}
          selectedDay={selectedDay}
          selectedSpotId={selectedSpotId}
          hoveredSpotId={hoveredSpotId ?? null}
          onSelectSpot={onSelectSpot}
          customLocation={customLocation}
          onLongPressMap={onLongPressMap}
        />
      </BaseMap>
    </MapProvider>
  );
}

/**
 * Zoom level at which we start drawing individual spot markers. Below this
 * the markers crowd together so we render a single region marker instead.
 * Calibrated so the default fit-to-region viewport (around zoom 6 for the
 * widest regions) lands above the threshold. Pinching all the way out to
 * Sweden-wide drops below it and the region disc reappears.
 */
const SPOT_MARKER_MIN_ZOOM = 5.5;

/** Restore from sessionStorage only if the entry is fresh (< 5s old) so
 *  fresh page loads don't pick up stale state from earlier sessions. */
const RECENT_MS = 5_000;

function readRecentView(): { lng: number; lat: number; zoom: number } | null {
  try {
    const raw = sessionStorage.getItem("listMapView");
    if (!raw) return null;
    const v = JSON.parse(raw) as {
      lng: number;
      lat: number;
      zoom: number;
      ts?: number;
    };
    if (!v.ts || Date.now() - v.ts > RECENT_MS) return null;
    return { lng: v.lng, lat: v.lat, zoom: v.zoom };
  } catch {
    return null;
  }
}

/** A user clicking a spot marker, then the card → detail page → back
 *  expects to see the same spot still highlighted on the map. We persist
 *  the selection (with the surrounding viewport so the camera doesn't
 *  re-fit) here for that round-trip to feel continuous. The TTL is
 *  generous since the user might dwell on detail for a while. */
const SELECTION_STORAGE_KEY = "__bw:mapSelectedSpot";
const SELECTION_TTL_MS = 5 * 60 * 1000;

interface StoredSelection {
  id: string;
  regionId: Region;
  lng: number;
  lat: number;
  zoom: number;
  ts: number;
}

function readStoredSelection(regionId: Region): StoredSelection | null {
  try {
    const raw = sessionStorage.getItem(SELECTION_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as StoredSelection;
    if (!v.ts || Date.now() - v.ts > SELECTION_TTL_MS) return null;
    if (v.regionId !== regionId) return null;
    return v;
  } catch {
    return null;
  }
}

function writeStoredSelection(entry: Omit<StoredSelection, "ts">): void {
  sessionStorage.setItem(
    SELECTION_STORAGE_KEY,
    JSON.stringify({ ...entry, ts: Date.now() }),
  );
}

function clearStoredSelection(): void {
  sessionStorage.removeItem(SELECTION_STORAGE_KEY);
}

/** Long-press threshold (ms) before we treat a press as "drop a pin"
 *  rather than a tap. 500ms is the iOS context-menu default — feels
 *  natural and doesn't fight a quick map tap. */
const LONG_PRESS_MS = 500;
/** Pixel slop allowed between press-start and timer-fire before we
 *  abort the long-press (treats the gesture as a pan instead). */
const LONG_PRESS_SLOP_PX = 8;

function SpotsLayer({
  spots,
  regionId,
  selectedDay,
  selectedSpotId,
  hoveredSpotId,
  onSelectSpot,
  customLocation,
  onLongPressMap,
}: SpotsMapProps) {
  const { current: map } = useMap();
  const [zoom, setZoom] = useState<number>(SPOT_MARKER_MIN_ZOOM);
  // Lazy-init from sessionStorage so a back-from-detail mount restores the
  // last viewport (selection itself is now owned by the parent page, which
  // does its own restoration if needed).
  const restoredSelection = useMemo(
    () => readStoredSelection(regionId),
    [regionId],
  );

  const region = regionById(regionId);
  const center = region?.coords ?? { lat: 62.5, lon: 16.5 };

  // Compute the bounding box for the region's spots (used by the region
  // marker fallback to "zoom into all spots").
  const bbox = useMemo(() => {
    if (spots.length === 0) return null;
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const s of spots) {
      if (s.coords.lon < minLon) minLon = s.coords.lon;
      if (s.coords.lon > maxLon) maxLon = s.coords.lon;
      if (s.coords.lat < minLat) minLat = s.coords.lat;
      if (s.coords.lat > maxLat) maxLat = s.coords.lat;
    }
    return { minLon, minLat, maxLon, maxLat };
  }, [spots]);

  // Initial camera placement. Priority:
  //   1. Restored selection (back-from-detail) — jump to the saved
  //      spot/zoom, no fitBounds, so the user lands exactly where they
  //      left off with the marker still selected.
  //   2. Recent regions-map viewport (regions → spots transition) — jump
  //      to it for continuity, then ease into the region bbox.
  //   3. Fresh load — fitBounds on the region's spots immediately.
  //
  // Runs once per mount, gated by `didInitialCameraRef`. The ref is
  // only set after a *successful* placement so that if the first run
  // has `bbox === null` (still loading) we retry when bbox arrives.
  // The deferred fitBounds for case (2) is stored in a separate ref
  // and cleaned up on unmount — keeping it out of the effect's
  // cleanup chain stops React from cancelling it whenever `bbox`'s
  // identity changes (which happens on every spot resort and was
  // killing the regions → spots ease-in).
  // Placement is tracked PER-MAP-INSTANCE (not a boolean) so React strict
  // mode's simulated unmount/remount — which recreates the underlying
  // maplibre map — gets a fresh placement. With a boolean ref the strict
  // mode dance would set the flag against map A, then the new map B
  // would see the flag set and skip placement entirely, leaving the
  // camera at the BaseMap default and the user staring at a zoomed-out
  // view of Sweden. (This was the regions → spots regression.)
  const placedForMapRef = useRef<unknown>(null);
  useEffect(() => {
    if (!map || placedForMapRef.current === map) return;
    // `cancelled` short-circuits the async `load` callback when the
    // effect cleans up (strict-mode simulated unmount, deps change,
    // real unmount). Without it the callback could fire on a stale map.
    let cancelled = false;
    const place = () => {
      if (cancelled || placedForMapRef.current === map) return;
      if (restoredSelection) {
        map.jumpTo({
          center: [restoredSelection.lng, restoredSelection.lat],
          zoom: restoredSelection.zoom,
        });
        placedForMapRef.current = map;
        return;
      }
      const stored = readRecentView();
      if (stored && bbox) {
        // Continuity from the regions map → spots map: instantly
        // teleport to the stored regions-wide view, then ease into the
        // region's spots bbox over 800ms.
        map.jumpTo({ center: [stored.lng, stored.lat], zoom: stored.zoom });
        map.setPadding(getMapFitPadding());
        map.fitBounds(
          [
            [bbox.minLon, bbox.minLat],
            [bbox.maxLon, bbox.maxLat],
          ],
          { duration: 800, maxZoom: 10 },
        );
        placedForMapRef.current = map;
        return;
      }
      if (stored) {
        map.jumpTo({ center: [stored.lng, stored.lat], zoom: stored.zoom });
        // bbox not ready yet — leave placedForMapRef unset so the next
        // dep change re-enters and runs the full ease-in.
        return;
      }
      if (bbox) {
        map.setPadding(getMapFitPadding());
        map.fitBounds(
          [
            [bbox.minLon, bbox.minLat],
            [bbox.maxLon, bbox.maxLat],
          ],
          { duration: 0, maxZoom: 10 },
        );
        placedForMapRef.current = map;
      }
    };
    // `fitBounds(duration > 0)` silently no-ops against an unloaded
    // map — wait for the load event before placing. `jumpTo` works
    // pre-load too but we wait anyway so the whole sequence runs
    // against a ready map.
    if (map.loaded()) {
      place();
    } else {
      map.once("load", place);
    }
    return () => {
      cancelled = true;
      map.off("load", place);
    };
  }, [map, bbox, restoredSelection]);

  // Track zoom so we can swap between region marker and spot markers.
  useEffect(() => {
    if (!map) return;
    setZoom(map.getZoom());
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoom", onZoom);
    return () => {
      map.off("zoom", onZoom);
    };
  }, [map]);

  // When the user zooms out far enough that individual spot markers
  // collapse into the single region disc, there's nothing to anchor the
  // selection to. Drop any active selection (and its persisted entry).
  useEffect(() => {
    if (zoom < SPOT_MARKER_MIN_ZOOM && selectedSpotId !== null) {
      onSelectSpot(null);
      clearStoredSelection();
    }
  }, [zoom, selectedSpotId, onSelectSpot]);

  // Ease the camera to the selected spot whenever the selection changes
  // (whether triggered by a marker click or by the MapSpotCard's
  // prev/next chevrons). Padding-bottom leaves room for the floating
  // MapSpotCard at the bottom of the map so the marker centres in the
  // visible area above the card rather than behind it.
  useEffect(() => {
    if (!map || !selectedSpotId) return;
    const spot = spots.find((s) => s.id === selectedSpotId);
    if (!spot) return;
    const targetZoom = Math.max(map.getZoom(), 10);
    map.flyTo({
      center: [spot.coords.lon, spot.coords.lat],
      zoom: targetZoom,
      duration: 600,
      padding: { top: 0, right: 0, bottom: 240, left: 0 },
    });
    writeStoredSelection({
      id: selectedSpotId,
      regionId,
      lng: spot.coords.lon,
      lat: spot.coords.lat,
      zoom: targetZoom,
    });
  }, [selectedSpotId, map, spots, regionId]);

  // Note: we deliberately don't persist on moveend here — when the user
  // hits back after panning around the spots map, the regions map
  // should reset to the Sweden-wide default rather than zooming in on
  // the region they just left.

  // Long-press to drop a pin. A press that survives 500ms without
  // significant movement fires `onLongPressMap`. Any pan, finger lift,
  // or pinch cancels the timer so normal map interactions still work.
  // Right-click on desktop is treated as an instant long-press for
  // discoverability.
  const longPressRef = useRef<{
    timer: number | null;
    startX: number;
    startY: number;
  }>({ timer: null, startX: 0, startY: 0 });

  useEffect(() => {
    if (!map) return;
    const cancel = () => {
      if (longPressRef.current.timer !== null) {
        window.clearTimeout(longPressRef.current.timer);
        longPressRef.current.timer = null;
      }
    };

    const onStart = (e: { point: { x: number; y: number }; lngLat: { lng: number; lat: number }; originalEvent?: { touches?: TouchList } }) => {
      // Ignore multi-touch (pinch/zoom).
      if (e.originalEvent && "touches" in e.originalEvent && (e.originalEvent.touches?.length ?? 0) > 1) {
        cancel();
        return;
      }
      cancel();
      longPressRef.current.startX = e.point.x;
      longPressRef.current.startY = e.point.y;
      const { lng, lat } = e.lngLat;
      longPressRef.current.timer = window.setTimeout(() => {
        longPressRef.current.timer = null;
        onLongPressMap({ lat, lon: lng });
      }, LONG_PRESS_MS);
    };

    const onMove = (e: { point: { x: number; y: number } }) => {
      if (longPressRef.current.timer === null) return;
      const dx = e.point.x - longPressRef.current.startX;
      const dy = e.point.y - longPressRef.current.startY;
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP_PX) cancel();
    };

    const onContextMenu = (e: { lngLat: { lng: number; lat: number }; preventDefault?: () => void }) => {
      e.preventDefault?.();
      cancel();
      onLongPressMap({ lat: e.lngLat.lat, lon: e.lngLat.lng });
    };

    map.on("mousedown", onStart);
    map.on("touchstart", onStart);
    map.on("mousemove", onMove);
    map.on("touchmove", onMove);
    map.on("mouseup", cancel);
    map.on("touchend", cancel);
    map.on("touchcancel", cancel);
    map.on("dragstart", cancel);
    map.on("zoomstart", cancel);
    map.on("contextmenu", onContextMenu);

    return () => {
      cancel();
      map.off("mousedown", onStart);
      map.off("touchstart", onStart);
      map.off("mousemove", onMove);
      map.off("touchmove", onMove);
      map.off("mouseup", cancel);
      map.off("touchend", cancel);
      map.off("touchcancel", cancel);
      map.off("dragstart", cancel);
      map.off("zoomstart", cancel);
      map.off("contextmenu", onContextMenu);
    };
  }, [map, onLongPressMap]);

  // Ease the camera to the dropped pin so the MapSpotCard isn't covering
  // the marker. Same padding-bottom reservation as the spot-selection case.
  useEffect(() => {
    if (!map || !customLocation) return;
    const targetZoom = Math.max(map.getZoom(), 10);
    map.flyTo({
      center: [customLocation.lon, customLocation.lat],
      zoom: targetZoom,
      duration: 600,
      padding: { top: 0, right: 0, bottom: 240, left: 0 },
    });
  }, [customLocation, map]);

  const showSpotMarkers = zoom >= SPOT_MARKER_MIN_ZOOM;

  return (
    <>
      {!showSpotMarkers && spots.length > 0 && (
        <RegionMarker
          longitude={center.lon}
          latitude={center.lat}
          count={spots.length}
          label={region?.label ?? "Region"}
          onClick={() => {
            if (map && bbox) {
              map.setPadding(getMapFitPadding());
              map.fitBounds(
                [
                  [bbox.minLon, bbox.minLat],
                  [bbox.maxLon, bbox.maxLat],
                ],
                { duration: 800, maxZoom: 11 },
              );
            }
          }}
        />
      )}

      {showSpotMarkers &&
        spots.map((spot) => {
          const { dirDeg, speedMs } = dayMarkerData(spot, selectedDay);
          return (
            <SpotMarker
              key={spot.id}
              longitude={spot.coords.lon}
              latitude={spot.coords.lat}
              dirDeg={dirDeg}
              speedMs={speedMs}
              idealDirs={spot.ideal.dirs}
              selected={spot.id === selectedSpotId}
              hovered={spot.id === hoveredSpotId}
              onClick={() => {
                const next = spot.id === selectedSpotId ? null : spot.id;
                onSelectSpot(next);
                if (!next) clearStoredSelection();
              }}
            />
          );
        })}

      {customLocation && (
        <CustomLocationMarker
          longitude={customLocation.lon}
          latitude={customLocation.lat}
        />
      )}
    </>
  );
}
